import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Shield, FileText, Lock, AlertTriangle, Eye } from 'lucide-react';

const Legal = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const initialTab = queryParams.get('tab') || 'terms';
    
    const [activeTab, setActiveTab] = useState(initialTab);

    useEffect(() => {
        if (initialTab !== activeTab) {
            setActiveTab(initialTab);
        }
        window.scrollTo(0, 0);
    }, [initialTab]);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        navigate(`/legal?tab=${tabId}`, { replace: true });
    };

    const tabs = [
        { id: 'terms', icon: <FileText size={16} />, label: 'Terms of Service' },
        { id: 'privacy', icon: <Lock size={16} />, label: 'Privacy Policy' },
        { id: 'cookies', icon: <Eye size={16} />, label: 'Cookie Policy' },
        { id: 'aml-cft', icon: <Shield size={16} />, label: 'AML / CFT' },
        { id: 'risk', icon: <AlertTriangle size={16} />, label: 'Risk Disclosure' }
    ];

    const renderContent = () => {
        switch (activeTab) {
            case 'terms':
                return (
                    <div className="prose prose-invert max-w-none">
                        <h3>1. Acceptance of Terms</h3>
                        <p>By accessing or using the Zaptopay platform, you agree to comply with and be bound by these Terms of Service. Zaptopay is a specialized cryptocurrency-to-XAF exchange operating primarily within the CEMAC region.</p>
                        <h3>2. Services Provided</h3>
                        <p>Zaptopay provides instant liquidation services for digital assets including USDT, Bitcoin, and Ethereum, delivering fiat equivalent (XAF) via verified Mobile Money operators (MTN MoMo, Orange Money).</p>
                        <h3>3. Transaction Finality</h3>
                        <p>All cryptocurrency transactions sent to our provided wallet addresses are final and irreversible. Ensure you verify the wallet address and network before sending any funds.</p>
                    </div>
                );
            case 'privacy':
                return (
                    <div className="prose prose-invert max-w-none">
                        <h3>1. Information We Collect</h3>
                        <p>We collect essential information required to process your exchange, including your Mobile Money number, transactional WhatsApp number, and blockchain transaction IDs.</p>
                        <h3>2. How We Use Your Information</h3>
                        <p>Your data is used strictly for fulfilling exchange requests, providing customer support, and complying with anti-money laundering regulations in Cameroon.</p>
                        <h3>3. Data Security</h3>
                        <p>We employ end-to-end encryption for all communication and utilize bank-level security protocols for our databases to protect your personal and financial information.</p>
                    </div>
                );
            case 'cookies':
                return (
                    <div className="prose prose-invert max-w-none">
                        <h3>1. Use of Cookies</h3>
                        <p>Zaptopay uses cookies and similar tracking technologies to track activity on our platform and hold certain information to enhance your experience.</p>
                        <h3>2. Types of Cookies</h3>
                        <p>We primarily use functional cookies that are strictly necessary for the platform to function securely, such as maintaining admin sessions and preferences.</p>
                    </div>
                );
            case 'aml-cft':
                return (
                    <div className="prose prose-invert max-w-none">
                        <h3>Anti-Money Laundering (AML) & Combating the Financing of Terrorism (CFT)</h3>
                        <p>Zaptopay strictly adheres to international and regional CEMAC AML/CFT guidelines to prevent our platform from being used for illicit activities.</p>
                        <h3>1. Verification Procedures</h3>
                        <p>While we offer fast exchanges, we monitor all transaction patterns. Unusually large or suspicious transactions may require Enhanced Due Diligence (EDD), including ID verification (KYC).</p>
                        <h3>2. Reporting</h3>
                        <p>We reserve the right to report suspicious activity to the relevant financial intelligence units in Cameroon and cooperate fully with law enforcement requests.</p>
                    </div>
                );
            case 'risk':
                return (
                    <div className="prose prose-invert max-w-none">
                        <h3>1. Price Volatility</h3>
                        <p>Cryptocurrency prices are highly volatile. The final XAF payout is determined by the rate locked in at the exact moment your transaction is confirmed on the blockchain.</p>
                        <h3>2. Network Congestion</h3>
                        <p>While Zaptopay processes payouts in under 2 minutes, we are not responsible for delays caused by blockchain network congestion or mobile money operator downtimes.</p>
                        <h3>3. Self-Custody Risks</h3>
                        <p>You are solely responsible for ensuring you send funds on the correct network (e.g., TRC20 for USDT). Zaptopay cannot recover funds sent to incorrect addresses or networks.</p>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <section className="pt-28 md:pt-48 pb-24 min-h-screen relative overflow-hidden bg-bg-deep">
            {/* Background elements */}
            <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary/5 to-transparent -z-10"></div>
            
            <div className="container relative z-10 max-w-5xl">
                <div className="text-center mb-16" data-aos="fade-up">
                    <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-4">Legal <span className="text-primary italic">Center</span></h1>
                    <p className="text-text-muted text-lg max-w-2xl mx-auto">
                        Official documentation, compliance frameworks, and policies governing the use of Zaptopay.
                    </p>
                </div>

                <div className="glass p-6 md:p-10 border-white/10" data-aos="fade-up" data-aos-delay="100">
                    <div className="flex flex-col md:flex-row gap-8">
                        {/* Tabs Sidebar */}
                        <div className="w-full md:w-64 shrink-0 flex flex-col gap-2">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabChange(tab.id)}
                                    className={`flex items-center gap-3 px-5 py-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                                        activeTab === tab.id 
                                        ? 'bg-primary text-black shadow-[0_0_20px_rgba(0,255,136,0.3)]' 
                                        : 'bg-white/5 text-text-muted hover:bg-white/10 hover:text-white'
                                    }`}
                                >
                                    {tab.icon}
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 bg-black/40 border border-white/5 rounded-2xl p-6 md:p-10">
                            <h2 className="text-2xl font-black uppercase tracking-widest text-primary mb-8 border-b border-white/10 pb-4">
                                {tabs.find(t => t.id === activeTab)?.label}
                            </h2>
                            <div className="text-text-main leading-relaxed space-y-6 text-sm md:text-base">
                                {renderContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Legal;
